import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useAuth, getAuthHeaders } from './AuthContext';

const ReturnLoopContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const INACTIVITY_THRESHOLD = 5 * 60 * 1000; // 5 minutes of inactivity counts as "away"

export const ReturnLoopProvider = ({ children }) => {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [sharedData, setSharedData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inviteUrl, setInviteUrl] = useState(null);
  
  const lastActivityRef = useRef(Date.now());
  const wasInactiveRef = useRef(false);

  // Fetch shared view data
  const fetchSharedData = useCallback(async () => {
    if (!isAuthenticated) {
      setSharedData(null);
      setLoading(false);
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/return-loop/shared`, {
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setSharedData(data);
      }
    } catch (error) {
      console.warn('Failed to fetch Return Loop data:', error);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  // Track a return event
  const trackReturn = useCallback(async () => {
    if (!isAuthenticated) return;

    try {
      const response = await fetch(`${API_URL}/api/return-loop/track`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        // Refresh shared data after tracking
        fetchSharedData();
      }
    } catch (error) {
      console.warn('Failed to track return:', error);
    }
  }, [isAuthenticated, fetchSharedData]);

  // Create invite link
  const createInvite = useCallback(async () => {
    if (!isAuthenticated) {
      console.warn('Cannot create invite: not authenticated');
      return null;
    }

    try {
      const response = await fetch(`${API_URL}/api/return-loop/invite`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        setInviteUrl(data.invite_url);
        return data;
      } else {
        console.warn('Failed to create invite:', response.status);
      }
    } catch (error) {
      console.warn('Failed to create invite:', error);
    }
    return null;
  }, [isAuthenticated]);

  // Accept invite
  const acceptInvite = useCallback(async (inviteCode) => {
    if (!isAuthenticated) return null;

    try {
      const response = await fetch(`${API_URL}/api/return-loop/accept/${inviteCode}`, {
        method: 'POST',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        const data = await response.json();
        fetchSharedData();
        return data;
      } else {
        const error = await response.json();
        return { error: error.detail };
      }
    } catch (error) {
      console.warn('Failed to accept invite:', error);
      return { error: 'Failed to connect' };
    }
  }, [isAuthenticated, fetchSharedData]);

  // Disconnect from partner
  const disconnect = useCallback(async () => {
    if (!isAuthenticated) return false;

    try {
      const response = await fetch(`${API_URL}/api/return-loop/disconnect`, {
        method: 'DELETE',
        credentials: 'include',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        setSharedData(prev => prev ? { ...prev, connected: false, partner_name: null, partner_returns: null } : null);
        return true;
      }
    } catch (error) {
      console.warn('Failed to disconnect:', error);
    }
    return false;
  }, [isAuthenticated]);

  // Track activity for inactivity-based returns
  useEffect(() => {
    if (!isAuthenticated) return;

    const handleActivity = () => {
      const now = Date.now();
      const timeSinceLastActivity = now - lastActivityRef.current;
      
      // If user was inactive for threshold and is now active, track a return
      if (wasInactiveRef.current && timeSinceLastActivity > INACTIVITY_THRESHOLD) {
        trackReturn();
      }
      
      lastActivityRef.current = now;
      wasInactiveRef.current = false;
    };

    const checkInactivity = () => {
      const timeSinceLastActivity = Date.now() - lastActivityRef.current;
      if (timeSinceLastActivity > INACTIVITY_THRESHOLD) {
        wasInactiveRef.current = true;
      }
    };

    // Listen for user activity
    const events = ['mousedown', 'keydown', 'touchstart', 'scroll'];
    events.forEach(event => document.addEventListener(event, handleActivity, { passive: true }));

    // Periodically check for inactivity
    const inactivityInterval = setInterval(checkInactivity, 60000); // Check every minute

    return () => {
      events.forEach(event => document.removeEventListener(event, handleActivity));
      clearInterval(inactivityInterval);
    };
  }, [isAuthenticated, trackReturn]);

  // Initial fetch
  useEffect(() => {
    if (!authLoading) {
      fetchSharedData();
    }
  }, [authLoading, fetchSharedData]);

  const value = {
    sharedData,
    loading,
    inviteUrl,
    trackReturn,
    createInvite,
    acceptInvite,
    disconnect,
    refreshData: fetchSharedData
  };

  return (
    <ReturnLoopContext.Provider value={value}>
      {children}
    </ReturnLoopContext.Provider>
  );
};

export const useReturnLoop = () => {
  const context = useContext(ReturnLoopContext);
  if (!context) {
    throw new Error('useReturnLoop must be used within a ReturnLoopProvider');
  }
  return context;
};

export default ReturnLoopContext;
